function getRange(start: number, end: number): number[] {
  return Array(end - start + 1)
    .fill(0)
    .map((_, i) => i + start);
}

/**
 * Builds the array of page numbers (and ellipsis markers) to display in the
 * pagination control.
 *
 * @param currentPage - 1-based current page number.
 * @param pageCount   - Total number of pages.
 * @returns Array of page numbers and '...' ellipsis markers.
 */
export function buildPaginationItems(currentPage: number, pageCount: number): (number | '...')[] {
  let delta: number;
  if (pageCount <= 7) {
    delta = 7;
  } else {
    delta = currentPage > 4 && currentPage < pageCount - 3 ? 2 : 4;
  }

  const range = {
    start: Math.round(currentPage - delta / 2),
    end: Math.round(currentPage + delta / 2),
  };

  if (range.start - 1 === 1 || range.end + 1 === pageCount) {
    range.start += 1;
    range.end += 1;
  }

  let pages: number[] =
    currentPage > delta
      ? getRange(Math.min(range.start, pageCount - delta), Math.min(range.end, pageCount))
      : getRange(1, Math.min(pageCount, delta + 1));

  const withDots = (value: number, pair: (number | '...')[]): (number | '...')[] =>
    pages.length + 1 !== pageCount ? pair : [value];

  if (pages[0] !== 1) {
    pages = (withDots(1, [1, '...']) as number[]).concat(pages);
  }

  if (pages[pages.length - 1] < pageCount) {
    pages = pages.concat(withDots(pageCount, ['...', pageCount]) as number[]);
  }

  return pages as (number | '...')[];
}
